import type { TranslationKey } from "@/translations/index"

// Aligned with the VMS / ClientRequest vocabulary (RequestAccepted / Canceled) so all three pre-WO
// entities share one status set. "Accepted" / "Cancelled" are the pre-alignment ATS spellings, kept in
// the union as READ tolerance: stored documents carry them until the Phase 7 data migration rewrites
// them, and this deploy lands before that migration runs.
export type AtsPlacementStatus =
  | "New"
  | "PreVerificationRequested"
  | "PreVerificationReview"
  | "PreVerificationComplete"
  | "RequestAccepted"
  | "Canceled"
  | "OnHold"
  | "Accepted"
  | "Cancelled"

// Drives the Complete-tab OData filter (`Status ne '<each>'`), so it must list BOTH spellings until the
// migration has run everywhere — otherwise records still holding a legacy value leak into the active
// tabs.
export const COMPLETED_STATUSES: AtsPlacementStatus[] = [
  "RequestAccepted",
  "Canceled",
  "Accepted",
  "Cancelled",
]


export interface AtsPlacementItem {
  recordId: string
  atsPlacementId: number
  atsSource: string
  importSource: string
  status?: AtsPlacementStatus
  workerName?: string
  workerEmail?: string
  clientOrgName?: string
  workerOrgName?: string
  lineOfBusinessCode?: string
  profileTypeCode?: string
  startDate?: string
  endDate?: string
  billRate?: number
  billCurrencyCode?: string
  payRate?: number
  payCurrencyCode?: string
  jobTitle?: string
  assignedToUser?: { email: string; fullName?: string }
  assignedToKey?: string
  preVerificationRequestId?: string
  createdOn?: string
  completedOn?: string
}

// Keys that map directly to AtsPlacementItem fields — safe to use as sort/filter keys
export type AtsItemKey = keyof AtsPlacementItem

// Virtual column keys that have no matching field on AtsPlacementItem
export type AtsVirtualKey = "worker" | "actions" | "assignedTo"

export type AtsColumnKey = AtsItemKey | AtsVirtualKey

export interface AtsPlacementFilters {
  // Facet / sidebar filters
  statuses: AtsPlacementStatus[]
  lineOfBusinessCodes: string[]
  profileTypeCodes: string[]
  clientOrgNames: string[]
  workerOrgNames: string[]
  assignedToEmails: string[]

  // Text search
  workerName: string
  workerEmail: string

  // Date range (ISO date strings, empty = unbounded)
  startDateFrom: string
  startDateTo: string
  endDateFrom: string
  endDateTo: string
  createdOnFrom: string
  createdOnTo: string
  completedOnFrom: string
  completedOnTo: string

  // Rate range
  billRateMin: number | null
  billRateMax: number | null
  payRateMin: number | null
  payRateMax: number | null

  // Sort — constrained to actual item fields, never virtual column keys
  sortKey: AtsItemKey | null
  sortDirection: "asc" | "desc" | null

  // Pagination
  page: number
  pageSize: number

  // Visibility
  showCompleted: boolean
}

export const EMPTY_ATS_FILTERS: AtsPlacementFilters = {
  statuses: [],
  lineOfBusinessCodes: [],
  profileTypeCodes: [],
  clientOrgNames: [],
  workerOrgNames: [],
  assignedToEmails: [],
  workerName: "",
  workerEmail: "",
  startDateFrom: "",
  startDateTo: "",
  endDateFrom: "",
  endDateTo: "",
  createdOnFrom: "",
  createdOnTo: "",
  completedOnFrom: "",
  completedOnTo: "",
  billRateMin: null,
  billRateMax: null,
  payRateMin: null,
  payRateMax: null,
  sortKey: null,
  sortDirection: null,
  page: 1,
  pageSize: 25,
  showCompleted: false,
}

export interface AtsFacetOptions {
  statuses: AtsPlacementStatus[]
  lineOfBusinessCodes: string[]
  profileTypeCodes: string[]
  clientOrgNames: string[]
  workerOrgNames: string[]
  assignedToKeys: string[]
}

export const EMPTY_FACET_OPTIONS: AtsFacetOptions = {
  statuses: [],
  lineOfBusinessCodes: [],
  profileTypeCodes: [],
  clientOrgNames: [],
  workerOrgNames: [],
  assignedToKeys: [],
}

// Deliberately keeps the ATS-specific translation keys ("Accepted" / "Cancelled") rather than reusing
// the VMS / ClientRequest ones ("Request Accepted" / "Canceled"): the status CODES are now aligned, but
// ATS user-facing wording is unchanged. Legacy codes map to the same keys so a record still holding one
// renders identically before the Phase 7 status migration.
export const ATS_STATUS_TRANSLATION_KEY: Record<AtsPlacementStatus, TranslationKey> = {
  New: "statusNew",
  PreVerificationRequested: "statusPreVerificationRequested",
  PreVerificationReview: "statusPreVerificationReview",
  PreVerificationComplete: "statusPreVerificationComplete",
  RequestAccepted: "statusAtsAccepted",
  Canceled: "statusAtsCancelled",
  OnHold: "statusOnHold",
  Accepted: "statusAtsAccepted",
  Cancelled: "statusAtsCancelled",
}

